import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

import type { RoadmapNode } from "../../api/roadmap-service";

import { choiceRule } from "../../utils/roadmap-tree";
import Iconify from "../iconify";

const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    flexShrink: 0,
    maxWidth: 240,
    padding: theme.spacing(0.5, 1, 0.5, 1.5),
    borderRadius: 999,
    border: `1px dashed ${ theme.palette.warning.main }`,
    backgroundColor: alpha(theme.palette.warning.main, 0.08),
    whiteSpace: 'normal',
}));

export type RoadmapChoiceBoxProps = {
    node: RoadmapNode;
    /** How many alternatives are expected at the selected level. */
    count: number;
    levelName: (level: number) => string;
    collapsed: boolean;
    onToggle: () => void;
};

/**
 * A choice between its children, e.g. React, Angular or Vue.js: it says how many of them the selected level expects,
 * and its tooltip how that grows with the level.
 */
export function RoadmapChoiceBox({ node, count, levelName, collapsed, onToggle }: RoadmapChoiceBoxProps) {
    const alternatives = node.children.length;
    const tooltip = node.picks
        .map((pick) => `${ levelName(pick.level) }: ${ choiceRule(pick.count, alternatives).toLowerCase() }`)
        .join(' · ');

    return <StyledBox sx={ { opacity: node.planned ? 0.65 : 1 } } data-testid="roadmap-choice">
        <Tooltip title={ tooltip }>
            <Typography component="span" variant="caption" sx={ { flexGrow: 1 } }>
                <Typography component="span" variant="inherit" sx={ { fontWeight: 'fontWeightBold', mr: 0.5, whiteSpace: 'nowrap' } }>
                    { choiceRule(count, alternatives) }
                </Typography>
                <Typography component="span" variant="inherit" color="text.secondary">
                    { node.title }
                </Typography>
            </Typography>
        </Tooltip>

        <IconButton size="small" color="inherit" onClick={ onToggle } aria-label={ collapsed ? 'Expand' : 'Collapse' }>
            <Iconify icon={ collapsed ? 'eva:plus-circle-outline' : 'eva:minus-circle-outline' } sx={ { width: 16, height: 16 } }/>
        </IconButton>
    </StyledBox>;
}
